import { useEffect, useMemo, useRef, useState } from "react"
import axios from "axios"
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import Filter from "pages/purchase/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Modal, Pagination, Transition } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { useResultSet } from "repositories/db-repository"
import useBranchRepositories from "repositories/branch"
import Delete from "./delete"
import PurchaseExportActions from "./export-actions"
import ImportFactorySuratJalan from "./import-factory-surat-jalan"
import { getBranchList, getGenericSupplier, getParameter, getCurrentEntity, buildOutletFilterOptions, getDefaultOutletFilterValueFrom, resolveOutletFilterWith } from "helpers/general-helper"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import StatusInfo from "components/status-info" // 150726 (ATALLA-322)
import toast from "react-hot-toast"
import {
  apiBase,
  checkLegacyBukti,
  fetchGoodsReceiptForPo,
  fetchSatuanMap,
  normalizeText,
  parseLegacyDate,
  pickCostPrice,
  pickProductId,
  readFileAsText,
  resolveLegacyProductMappings,
  stripControlChars,
  todayIso,
  updateGoodsReceiptHeader,
} from "./import-utils"

// 150726 (ATALLA-322): penjelasan singkat tiap status PO utk tombol tanda tanya
// di samping filter status.
const PO_STATUS_INFO = [
  { value: "DRAFT", label: "DRAFT", description: "PO dibuat, menunggu approve." },
  { value: "SUBMITTED", label: "SUBMITTED", description: "PO diajukan, menunggu approve." },
  { value: "APPROVED", label: "APPROVED", description: "PO disetujui; utk PO internal stok HO sudah terpotong." },
  { value: "PARTIAL_RECEIVED", label: "PARTIAL_RECEIVED", description: "Barang diterima sebagian." },
  { value: "RECEIVED", label: "RECEIVED", description: "Barang diterima penuh." },
  { value: "CLOSED", label: "CLOSED", description: "PO ditutup manual." },
  { value: "CANCELLED", label: "CANCELLED", description: "PO dibatalkan; stok yang sudah terpotong dikembalikan." },
  { value: "REJECTED", label: "REJECTED", description: "PO ditolak sebelum approve." },
]

// 230726: total qty dari server sudah dalam SATUAN TAMPILAN PO (rumus pcs riil ÷
// faktor uom — paritas Total Qty form detail); pecahan lusin tampil 2 desimal ("2,50").
const qtyFmt = (v) => {
  const n = Number(v) || 0
  return Number.isInteger(n)
    ? String(n)
    : n.toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const parseLegacyDeliveryOrder = (text) => {
  const cleanText = String(text || "").replace(/\r/g, "\n")
  const lines = cleanText.split("\n")
  const normalizedLines = lines.map((line) => stripControlChars(line).trimEnd())

  const getHeaderValue = (label) => {
    const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const line = normalizedLines.find((row) => row.includes(label))
    if (!line) return ""
    const match = line.match(new RegExp(`${escapedLabel}\\s*:\\s*(.*?)(?=\\s{2,}\\S[^:]*\\s*:|$)`))
    return (match?.[1] || "").trim()
  }

  const items = []
  let insideItems = false

  for (const row of normalizedLines) {
    const line = row.trim()

    if (/^Kode Barang\s+Jumlah\s+Satuan/i.test(line)) {
      insideItems = true
      continue
    }

    if (!insideItems) continue
    if (!line || /^=+$/.test(line)) continue
    if (/^-+$/.test(line) || /Bersambung/i.test(line) || /^Jumlah\s*=/i.test(line)) {
      insideItems = false
      continue
    }

    const itemMatch = line.match(/^(.+?)\s+([0-9][0-9.,]*)\s+([A-Za-z0-9./-]+)$/)
    if (!itemMatch) continue

    const productName = itemMatch[1].replace(/\s+/g, " ").trim()
    // Di file PRN ini koma adalah pemisah ribuan (mis. "2,500" => 2500,
    // "1,250,000" => 1250000). Buang koma, titik dipertahankan untuk desimal.
    const qty = Number(itemMatch[2].replace(/,/g, ""))
    const uom = itemMatch[3].trim()

    if (productName && Number.isFinite(qty) && qty > 0) {
      items.push({ productName, qty, uom })
    }
  }

  const mergedItems = Array.from(
    items
      .reduce((map, item) => {
        const key = `${normalizeText(item.productName)}|${normalizeText(item.uom)}`
        const current = map.get(key)
        map.set(key, current ? { ...current, qty: current.qty + item.qty } : item)
        return map
      }, new Map())
      .values()
  )

  const dateText = getHeaderValue("Tanggal")

  return {
    legacyNoBukti: getHeaderValue("No.Bukti"),
    legacyDoNo: getHeaderValue("No.DO"),
    customerName: getHeaderValue("Customer"),
    address: getHeaderValue("Alamat"),
    poDate: parseLegacyDate(dateText),
    items: mergedItems,
  }
}

function ImportLegacyDO({ branchSelection, currentBranch, supplierSelection, userName, onSuccess }) {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [fileName, setFileName] = useState("")
  const [parsed, setParsed] = useState(null)
  const [outletId, setOutletId] = useState(currentBranch?.id ?? "")
  const [supplierId, setSupplierId] = useState("")
  const [errors, setErrors] = useState([])
  const [warnings, setWarnings] = useState([])
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setFileName("")
    setParsed(null)
    setErrors([])
    setWarnings([])
    setSubmitting(false)
    setOutletId(currentBranch?.id ?? "")
    setSupplierId("")
  }

  const closeModal = () => {
    if (submitting) return
    setIsOpen(false)
    reset()
  }

  const onFileChange = async (event) => {
    const file = event.target.files?.[0]
    setErrors([])
    setWarnings([])
    setParsed(null)
    setFileName(file?.name || "")

    if (!file) return

    try {
      const text = await readFileAsText(file)
      const result = parseLegacyDeliveryOrder(text)

      if (!result.items.length) {
        setErrors(["Item DO tidak terbaca. Pastikan file berisi tabel Kode Barang, Jumlah, Satuan."])
      }

      setParsed(result)
    } catch (error) {
      setErrors([error?.message || "Gagal membaca file DO"])
    }
  }

  const buildItems = async () => {
    const notFound = []
    const matchWarnings = []
    const warningSet = new Set()
    const resolved = []

    const addWarning = (message) => {
      if (!message || warningSet.has(message)) return
      warningSet.add(message)
      matchWarnings.push(message)
    }

    const [mappingMap, satuanMap] = await Promise.all([
      resolveLegacyProductMappings(
        parsed.items.map((item) => item.productName),
        outletId || getParameter("REMAX_HQ_OUTLET_ID") || currentBranch?.id || ""
      ),
      fetchSatuanMap(),
    ])

    for (const item of parsed.items) {
      const mapping = mappingMap.get(normalizeText(item.productName))

      if (!mapping || !pickProductId(mapping)) {
        notFound.push(item.productName)
        continue
      }

      if (!pickCostPrice(mapping)) {
        addWarning(`Harga active belum ada: ${item.productName}`)
      }

      // Konversi qty legacy uom (mis. BOX/LUSIN) ke pcs berdasarkan master Satuan.
      // Pcs di master harus qty_per_unit = 1. Jika uom tidak dikenal, fallback 1:1 + warning.
      const uomKey = normalizeText(item.uom)
      const factor = satuanMap.get(uomKey)
      if (factor === undefined) {
        addWarning(`Satuan "${item.uom}" tidak ada di master — dianggap 1:1 ke pcs.`)
      }
      const conversion = Number.isFinite(factor) && factor > 0 ? factor : 1
      // Hasil konversi ke pcs dibulatkan ke integer (mis. 3.92 LSN × 12 = 47.04 → 47 PCS)
      const qtyInPcs = Math.round(Number(item.qty) * conversion)

      resolved.push({
        product_unit_id: String(pickProductId(mapping)),
        uom: 'PCS',
        qty_ordered: qtyInPcs,
        qty_received: 0,
        cost_price: pickCostPrice(mapping),
        sell_price_ref: Number(mapping?.sell_price_ref ?? 0),
      })
    }

    const merged = Array.from(
      resolved
        .reduce((map, item) => {
          const current = map.get(item.product_unit_id)
          map.set(
            item.product_unit_id,
            current
              ? { ...current, qty_ordered: Number(current.qty_ordered) + Number(item.qty_ordered) }
              : item
          )
          return map
        }, new Map())
        .values()
    )

    return { items: merged, notFound, matchWarnings }
  }

  const submitImport = async (event) => {
    event.preventDefault()
    setErrors([])
    setWarnings([])

    if (!outletId) {
      toast.error("Pilih outlet terlebih dahulu")
      return
    }

    if (!supplierId) {
      toast.error("Pilih supplier terlebih dahulu")
      return
    }

    if (!parsed?.items?.length) {
      toast.error("Upload file DO terlebih dahulu")
      return
    }

    try {
      setSubmitting(true)
      nProgress.start()

      if (parsed.legacyNoBukti) {
        const checkJson = await checkLegacyBukti(parsed.legacyNoBukti)

        if (checkJson?.exists) {
          const existing = checkJson?.po
          const label = existing?.po_number ? ` (PO ${existing.po_number})` : ""
          setErrors([`No.Bukti "${parsed.legacyNoBukti}" sudah pernah diimport${label}.`])
          return
        }
      }

      const { items, notFound, matchWarnings } = await buildItems()

      if (notFound.length) {
        setErrors(notFound.map((name) => `Mapping produk lama belum dibuat: ${name}`))
        if (matchWarnings.length) setWarnings(matchWarnings)
        return
      }

      const base = apiBase()
      const response = await fetch(`${base}/purchase-orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          outlet_id: Number(outletId),
          supplier_id: supplierId,
          po_date: parsed.poDate || todayIso(),
          po_type: "REGULAR",
          status: "DRAFT",
          remarks: [
            parsed.legacyDoNo ? `Import DO lama: ${parsed.legacyDoNo}` : null,
            parsed.legacyNoBukti ? `No.Bukti: ${parsed.legacyNoBukti}` : null,
            parsed.customerName ? `Customer: ${parsed.customerName}` : null,
            parsed.address ? `Alamat: ${parsed.address}` : null,
          ].filter(Boolean).join("\n"),
          createdBy: userName,
          items,
        }),
      })

      const json = await response.json().catch(() => ({}))
      if (!response.ok) {
        const fieldErrors = json?.errors && typeof json.errors === "object"
          ? Object.values(json.errors).flat().filter(Boolean).join("; ")
          : ""
        throw new Error(fieldErrors || json?.message || "Gagal membuat PO dari DO")
      }

      const poId = json?.data?.id || json?.id
      if (!poId) throw new Error("PO berhasil dibuat, tapi ID PO tidak ditemukan dari response")

      const approveResponse = await fetch(`${base}/purchase-orders/${poId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // skip_ho_outflow: approve dari import TIDAK memotong stok sisi supplier
        // internal (Vision/HO) — potong stok terjadi saat GR di-post.
        // seed_full_received: GR draft hasil import tetap terisi qty dokumen
        // (default approve biasa kini seed 0 — diisi gudang di detail GR).
        body: JSON.stringify({ approvedBy: userName, skip_ho_outflow: true, seed_full_received: true }),
      })
      const approveJson = await approveResponse.json().catch(() => ({}))
      if (!approveResponse.ok) throw new Error(approveJson?.message || "PO dibuat, tapi gagal approve")

      const receipt = await fetchGoodsReceiptForPo(poId)
      if (!receipt?.id) throw new Error("PO sudah approved, tapi Goods Receipt draft tidak ditemukan")

      try {
        await updateGoodsReceiptHeader(receipt.id, {
          receipt_date: parsed.poDate || todayIso(),
          supplier_do_number: parsed.legacyDoNo || null,
          remarks: [
            parsed.legacyNoBukti ? `Import DO lama No.Bukti: ${parsed.legacyNoBukti}` : null,
            parsed.customerName ? `Customer: ${parsed.customerName}` : null,
            parsed.address ? `Alamat: ${parsed.address}` : null,
          ].filter(Boolean).join("\n") || null,
        })
      } catch (headerError) {
        toast.error(headerError?.message || "Goods Receipt dibuat, tapi header belum ter-update")
      }

      if (matchWarnings.length) setWarnings(matchWarnings)
      toast.success("Import DO berhasil. PO approved dan Goods Receipt draft dibuat.")
      setIsOpen(false)
      reset()
      onSuccess?.()
      navigate(`/goods-receipt/${receipt.id}/edit`)
    } catch (error) {
      setErrors([error?.message || "Gagal import DO"])
      toast.error(error?.message || "Gagal import DO")
    } finally {
      nProgress.done()
      setSubmitting(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center px-4 py-3 space-x-2 text-xs transition border border-neutral-200 bg-neutral-50 rounded-xl active:hover:scale-90"
      >
        <span>Import Surat Jalan</span>
      </button>

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        title="Import Surat Jalan Lama"
        content={(
          <form onSubmit={submitImport} className="mt-6 space-y-5 text-xs">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="block font-medium text-neutral-700">Outlet</label>
                <select
                  value={outletId || ""}
                  onChange={(event) => setOutletId(event.target.value)}
                  className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                >
                  <option value="">Select Outlet</option>
                  {(branchSelection || []).map((branch) => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="block font-medium text-neutral-700">Supplier</label>
                <select
                  value={supplierId || ""}
                  onChange={(event) => setSupplierId(event.target.value)}
                  className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                >
                  <option value="">Select Supplier</option>
                  {(supplierSelection || []).map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label className="block font-medium text-neutral-700">File DO</label>
              <input
                type="file"
                accept=".prn,.PRN,.txt"
                onChange={onFileChange}
                className="block w-full text-xs text-neutral-700 border border-neutral-200 rounded-xl cursor-pointer file:mr-3 file:px-3 file:py-2 file:text-xs file:font-medium file:border-0 file:rounded-xl file:bg-brand-800 file:text-white hover:file:bg-brand-900"
              />
              {fileName && <p className="text-[11px] text-neutral-500">Selected file: <span className="font-medium">{fileName}</span></p>}
            </div>

            {parsed && (
              <div className="overflow-hidden border border-neutral-200 rounded-xl">
                <div className="grid grid-cols-2 gap-2 p-3 bg-neutral-50 text-[11px] text-neutral-700">
                  <div>No.Bukti: <b>{parsed.legacyNoBukti || "-"}</b></div>
                  <div>No.DO: <b>{parsed.legacyDoNo || "-"}</b></div>
                  <div>Tanggal: <b>{parsed.poDate || "-"}</b></div>
                  <div>Total Qty: <b>{parsed.items.reduce((sum, item) => sum + item.qty, 0)}</b></div>
                  <div className="col-span-2">Customer: <b>{parsed.customerName || "-"}</b></div>
                </div>
                <div className="max-h-56 overflow-auto">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-3 py-2 text-[11px] font-medium text-left text-neutral-500 uppercase">Kode Barang</th>
                        <th className="px-3 py-2 text-[11px] font-medium text-right text-neutral-500 uppercase">Qty</th>
                        <th className="px-3 py-2 text-[11px] font-medium text-left text-neutral-500 uppercase">Satuan</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {parsed.items.slice(0, 80).map((item) => (
                        <tr key={`${item.productName}-${item.uom}`} className="hover:bg-brand-50 transition-colors">
                          <td className="px-3 py-2 text-[11px] text-neutral-800">{item.productName}</td>
                          <td className="px-3 py-2 text-[11px] text-right text-neutral-800">{item.qty}</td>
                          <td className="px-3 py-2 text-[11px] text-neutral-800">{item.uom}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {warnings.length > 0 && (
              <div className="p-3 text-[11px] border rounded-xl border-amber-200 bg-amber-50 text-amber-800 max-h-32 overflow-auto">
                <div className="mb-1 font-semibold">Match tidak exact:</div>
                <ul className="pl-4 space-y-1 list-disc">
                  {warnings.map((message, index) => <li key={index}>{message}</li>)}
                </ul>
              </div>
            )}

            {errors.length > 0 && (
              <div className="p-3 text-[11px] border rounded-xl border-red-200 bg-red-50 text-red-700 max-h-40 overflow-auto">
                <div className="mb-1 font-semibold">Error:</div>
                <ul className="pl-4 space-y-1 list-disc">
                  {errors.map((message, index) => <li key={index}>{message}</li>)}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-3 text-xs font-semibold text-white bg-brand-800 rounded-xl hover:bg-brand-900 disabled:opacity-60"
              >
                {submitting ? "Memproses..." : "Import dan Buka GR"}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={closeModal}
                className="px-5 py-3 text-xs font-semibold border border-neutral-200 rounded-xl text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      />
    </>
  )
}

export default function List() {
  const { can, currentBranch, user, isSuper, canFilterAllOutlets } = useAuth()
  const navigate = useNavigate() // 200726: dipakai onCopyPo (redirect ke edit PO hasil copy)
  const { title, transition, setTransition } = useOutletContext()

  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  // 200726 (keputusan user): filter entity/branch di halaman PO DIHILANGKAN — dropdown
  // outlet bebas untuk semua user (PO lintas entitas terlihat, mis. PO toko Atalla yang
  // mengarah ke supplier Vision harus terlihat orang Vision).
  const isBranchLocked = false

  const page = searchParams.get("page")
  const status = searchParams.get("status")
  // 210726: default filter = outlet user login. Param absen (null) => default ke
  // currentBranch; sentinel "all" = "Semua Outlet" (dibedakan dari absen supaya
  // pilihan Semua Outlet bisa persist dan tidak balik ke default outlet user).
  // 240726 (permintaan user): filter outlet seragam (grup Semua Outlet/HO + individual).
  // Nilai default + resolusi dihitung di bawah (butuh allOutlets lintas-entitas).
  const rawOutletParam = searchParams.get("outlet_id")
  const dateFrom = searchParams.get("date_from") ?? ""
  const dateTo = searchParams.get("date_to") ?? ""
  const searchParam = searchParams.get("search") ?? ""

  // Local state untuk search (submit on Enter)
  const [search, setSearch] = useState(searchParam)
  const searchInput = useRef(null)

  useEffect(() => setSearch(searchParam), [searchParam])

  const branchSelection = getBranchList()
  const supplierSelection = getGenericSupplier()
  const branchIdList = useMemo(() => {
    if (!branchSelection) return []
    return branchSelection.map((x) => x.id).filter(Boolean)
  }, [branchSelection])

  // 210726: list PO menampilkan PO lintas semua outlet/entity (isBranchLocked=false),
  // jadi peta nama outlet TIDAK boleh hanya dari branch_list per-user (getBranchList) —
  // outlet di luar itu akan tampil sebagai id mentah (mis. Admin HO 2). Ambil daftar
  // outlet lengkap (unrestricted) — sumber yang sama dgn form PO.
  const { data: allOutletData } = useBranchRepositories({ paginate: false, unrestricted: true })
  const allOutlets = useMemo(
    () => (Array.isArray(allOutletData) ? allOutletData : allOutletData?.data ?? []),
    [allOutletData]
  )

  // Map outlet_id -> nama outlet untuk ditampilkan di tabel. Gabungan daftar outlet
  // lengkap + branch_list per-user (fallback bila fetch unrestricted belum siap).
  const branchNameById = useMemo(() => {
    const m = {}
    ;[...(allOutlets || []), ...(branchSelection || [])].forEach((x) => {
      if (x?.id != null && x?.name != null) m[String(x.id)] = x.name
    })
    return m
  }, [allOutlets, branchSelection])

  // 240726 (permintaan user): opsi filter outlet LINTAS-ENTITAS (dari allOutlets, bukan
  // getBranchListForFilter) — supaya user Vision tetap bisa memilih outlet HO Atalla
  // (id 1) & grup HO utk melihat PO Atalla-HO yang men-supplier ke Vision. Grup "Semua
  // Outlet" tetap adaptif entitas user; individual + grup HO ikut allOutlets.
  const outletOptions = useMemo(
    () => buildOutletFilterOptions(allOutlets, getCurrentEntity()),
    [allOutlets]
  )
  const outletSelectValue = isBranchLocked
    ? String(currentBranch?.id ?? "")
    : (rawOutletParam ?? getDefaultOutletFilterValueFrom(outletOptions, currentBranch?.id))
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilterWith(outletSelectValue, outletOptions)

  const { data: result_set, isLoading, mutate } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    `purchase_transaction`,
    {
      outlet_id: resolvedOutletCsv || undefined,
      branch_list: JSON.stringify(branchIdList),
      page,
      record_per_page: 5,
      search: searchParam || undefined,
      latest_flow_detail_id: status,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }
  )

  // const { data: list_flow } = useResultSet(
  //   `${process.env.REACT_APP_INVENTORY_API_URL}`,
  //   `list_flow`,
  //   {}
  // )

  useEffect(() => {
    if (transition?.reverse) setTransition({ type: "slide", reverse: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (searchInput.current) searchInput.current.focus()
  }, [])

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  // 230726 (permintaan user): simplifikasi toolbar — dua tombol import digabung ke
  // satu dropdown "Import", dan filter tanggal dipindah ke popover.
  const [importMenuOpen, setImportMenuOpen] = useState(false)
  const [dateFilterOpen, setDateFilterOpen] = useState(false)

  const patchSearchParams = (patch, opts = {}) => {
    const next = new URLSearchParams(searchParams)

    Object.entries(patch || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k)
      else next.set(k, String(v))
    })

    // reset page ketika filter berubah
    if (patch && (Object.prototype.hasOwnProperty.call(patch, "search") || Object.prototype.hasOwnProperty.call(patch, "status") || Object.prototype.hasOwnProperty.call(patch, "outlet_id") || Object.prototype.hasOwnProperty.call(patch, "date_from") || Object.prototype.hasOwnProperty.call(patch, "date_to"))) {
      next.delete("page")
    }

    setSearchParams(next, opts)
  }

  const updateFilter = (value) => patchSearchParams(value)
  const removeFilter = () => setSearchParams({})

  const onSearchChange = (val) => setSearch(val)

  const onSearchSubmit = () => patchSearchParams({ search: search, page: 1 })

  const generatePaginationParam = () => {
    let tmp = ""
    if (status) tmp += `&status=${status}`
    if (outletSelectValue) tmp += `&outlet_id=${outletSelectValue}`
    if (dateFrom) tmp += `&date_from=${dateFrom}`
    if (dateTo) tmp += `&date_to=${dateTo}`
    if (searchParams.has("search")) tmp += `&search=${searchParams.get("search")}`
    return tmp
  }

  // normalize list dari response baru
  const list = useMemo(() => {
    if (Array.isArray(result_set?.data?.data)) return result_set.data?.data
    return []
  }, [result_set])

  // untuk Pagination component (kalau komponen kamu masih expect fields ini)
  const paginationLinks = result_set?.data?.links
  const total = result_set?.data?.total
  const from = result_set?.data?.from
  const to = result_set?.data?.to

  // ATALLA-322: penjelasan tiap status — tampil sebagai tooltip saat hover badge.
  const STATUS_DESCRIPTIONS = {
    DRAFT: "PO baru dibuat, belum disetujui — masih bisa diedit.",
    SUBMITTED: "PO sudah diajukan, menunggu approval.",
    APPROVED: "PO disetujui — GR draft otomatis dibuat, siap dikirim/diterima.",
    PARTIAL_RECEIVED: "Barang baru diterima sebagian, sisanya masih outstanding.",
    RECEIVED: "Seluruh barang sudah diterima.",
    REJECTED: "PO ditolak approver — lihat alasannya di catatan.",
    CANCELLED: "PO dibatalkan, tidak diproses lagi.",
    CLOSED: "PO ditutup manual — sisa qty yang belum datang tidak akan diterima.",
  }

  const statusBadge = (st) => {
    const s = String(st || "").toUpperCase()
    const base = "inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-medium cursor-help"
    const title = STATUS_DESCRIPTIONS[s] || ""
    if (s === "APPROVED") return <span title={title} className={`${base} bg-blue-50 text-blue-700`}>{st}</span>
    if (s === "RECEIVED") return <span title={title} className={`${base} bg-green-50 text-green-700`}>{st}</span>
    if (s === "CLOSED") return <span title={title} className={`${base} bg-green-50 text-emerald-700`}>{st}</span>
    if (s === "PARTIAL_RECEIVED") return <span title={title} className={`${base} bg-amber-50 text-amber-700`}>{st}</span>
    if (s === "SUBMITTED") return <span title={title} className={`${base} bg-sky-50 text-sky-700`}>{st}</span>
    if (s === "DRAFT") return <span title={title} className={`${base} bg-neutral-100 text-neutral-700`}>{st}</span>
    if (s === "CANCELLED" || s === "REJECTED") return <span title={title} className={`${base} bg-red-50 text-red-700`}>{st}</span>
    return <span title={title} className={`${base} bg-neutral-100 text-neutral-700`}>{st || "-"}</span>
  }

  const formatDateTime = (value) => {
    if (!value) return "-"

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value

    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date)
  }

  const postJson = async (path, body = null) => {
    const url = `${process.env.REACT_APP_INVENTORY_API_URL.replace(/\/$/, "")}${path}`
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body ? JSON.stringify(body) : null,
      // credentials: "include", // kalau auth cookie-based
    })

    const json = await resp.json().catch(() => ({}))
    if (!resp.ok) {
      const msg = json?.message || "Request gagal"
      const err = json?.error ? `: ${json.error}` : ""
      throw new Error(msg + err)
    }
    return json
  }

  const [actingId, setActingId] = useState(null)

  const onApprove = async (po) => {
    if (!po?.id) return
    if (!window.confirm(`Approve PO ${po.po_number || ""}?`)) return

    try {
      setActingId(po.id)
      await postJson(`/purchase-orders/${po.id}/approve`)
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal approve")
    } finally {
      setActingId(null)
    }
  }

  const onCancel = async (po) => {
    if (!po?.id) return
    const reason = window.prompt(`Cancel PO ${po.po_number || ""}\nAlasan (optional):`, "")
    if (reason === null) return // user cancel prompt

    try {
      setActingId(po.id)
      await postJson(`/purchase-orders/${po.id}/cancel`, reason ? { reason } : null)
      await mutate()
    } catch (e) {
      alert(e.message || "Gagal cancel")
    } finally {
      setActingId(null)
    }
  }

  const onClose = async (po) => {
    if (!po?.id) return

    if (!window.confirm(`Close PO ${po.po_number || ""}?`)) return

    try {
      setActingId(po.id)

      await postJson(`/purchase-orders/${po.id}/close`)

      await mutate()
    } catch (e) {
      alert(e.message || "Gagal close PO")
    } finally {
      setActingId(null)
    }
  }

  const canClose = (po) => {
    const st = String(po?.status || "").toUpperCase()
    return ["PARTIAL_RECEIVED", "RECEIVED"].includes(st)
  }

  // ATALLA-298: PO parsial → buat GR draft berisi SISA outstanding utk penerimaan berikutnya.
  const canReceiveRemainder = (po) => {
    const st = String(po?.status || "").toUpperCase()
    return st === "PARTIAL_RECEIVED"
  }

  // 200726 (permintaan user): Copy PO — buat PO DRAFT baru berisi produk yang sama,
  // lalu langsung buka halaman edit-nya supaya bisa diubah-ubah sebelum diproses.
  // 220726 (permintaan user): jangan langsung buat draft — tampilkan modal untuk
  // memilih Supplier & Outlet tujuan PO baru dulu, baru kirim ke endpoint copy.
  const [copyPo, setCopyPo] = useState(null)
  const [copySupplierId, setCopySupplierId] = useState("")
  const [copyOutletId, setCopyOutletId] = useState("")
  const [copySubmitting, setCopySubmitting] = useState(false)
  // 220726: peringatan "PO ini sudah pernah dicopy" — daftar copy yang belum cancel.
  const [copyExisting, setCopyExisting] = useState([])

  const onCopyPo = (po) => {
    setCopyPo(po)
    setCopySupplierId(String(po.supplier_id ?? ""))
    setCopyOutletId(String(po.outlet_id ?? ""))
    setCopyExisting([])

    const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
    axios
      .get(`${base}/purchase-orders/${po.id}/copy-check`)
      .then((res) => {
        const copies = Array.isArray(res?.data?.copies) ? res.data.copies : []
        setCopyExisting(copies)
      })
      // Gagal cek tidak memblokir copy — peringatan saja yang tidak tampil.
      .catch(() => setCopyExisting([]))
  }

  const closeCopyModal = () => {
    if (copySubmitting) return
    setCopyPo(null)
    setCopySupplierId("")
    setCopyOutletId("")
    setCopyExisting([])
  }

  const submitCopyPo = async (event) => {
    event.preventDefault()
    if (!copyPo) return
    if (!copySupplierId) { toast.error("Pilih supplier untuk PO baru"); return }
    if (!copyOutletId) { toast.error("Pilih outlet tujuan untuk PO baru"); return }

    setCopySubmitting(true)
    setActingId(copyPo.id)
    try {
      const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
      const res = await axios.post(`${base}/purchase-orders/${copyPo.id}/copy`, {
        createdBy: user?.name || undefined,
        supplier_id: Number(copySupplierId),
        outlet_id: Number(copyOutletId),
      })
      const newPo = res?.data?.data
      await mutate()
      if (newPo?.id) {
        toast.success(`PO tercopy: ${newPo.po_number || newPo.id} (DRAFT). Silakan sesuaikan lalu simpan.`)
        navigate(`${newPo.id}/edit`, { state: { back: title, from: location?.pathname, transition: "slide" } })
      }
      setCopyPo(null)
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Gagal meng-copy PO")
    } finally {
      setCopySubmitting(false)
      setActingId(null)
    }
  }

  const onReceiveRemainder = async (po) => {
    if (!window.confirm(`Buat Goods Receipt untuk SISA item PO ${po.po_number || po.id}?`)) return
    setActingId(po.id)
    try {
      const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")

      // ATALLA-344: cek dulu apakah sudah ada GR draft (belum di-posting) untuk PO ini.
      // Kalau ada, jangan diam-diam bikin lagi — beri peringatan supaya admin
      // menyelesaikan/posting draft yang sudah ada dulu.
      const draftRes = await axios.get(`${base}/goods-receipts`, {
        params: { po_id: po.id, status: "DRAFT", paginate: false },
      })
      const draftPayload = draftRes?.data?.data
      const draftRows = Array.isArray(draftPayload)
        ? draftPayload
        : (draftPayload?.data ?? [])
      if (draftRows.length > 0) {
        const existing = draftRows[0]
        alert(
          `Sudah ada GR draft yang belum di-posting untuk PO ${po.po_number || po.id}` +
          (existing?.gr_number ? ` (No. ${existing.gr_number})` : "") +
          `.\nSelesaikan / posting draft tersebut dulu di menu Goods Receipt sebelum membuat GR sisa baru.`
        )
        return
      }

      await axios.post(`${base}/purchase-orders/${po.id}/ensure-gr-draft`, {})
      alert("Draft GR sisa dibuat. Lanjutkan di menu Goods Receipt: cek qty yang datang lalu posting.")
      await mutate()
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Gagal membuat GR sisa")
    } finally {
      setActingId(null)
    }
  }

  const canApprove = (po) => {
    const st = String(po?.status || "").toUpperCase()
    return st === "DRAFT"
  }

  const canPrint = (po) => {
    // 270726 (permintaan user): Faktur & Surat Jalan bisa dicetak sejak PO di-SUBMIT
    // (bukan lagi setelah approve); print DO boleh sejak DRAFT (dokumen picking
    // gudang + cek stok HO) — menu tetap tampil utk DRAFT berisi DO saja
    // (gating per-item di PurchaseExportActions via poStatus).
    const st = String(po?.status || "").toUpperCase()
    return ["DRAFT", "SUBMITTED", "APPROVED", "PARTIAL_RECEIVED", "RECEIVED", "CLOSED"].includes(st)
  }

  const canCancel = (po) => {
    const st = String(po?.status || "").toUpperCase()
    return !["CANCELLED", "RECEIVED", "PARTIAL_RECEIVED","REJECTED"].includes(st)
  }

  return (
    <Transition type="fade">
      <div className="pb-4 space-y-6 lg:pb-8">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center sm:space-x-2">
            <div className="relative hidden sm:block">
              <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <circle cx={10} cy={10} r={7}></circle>
                  <line x1={21} y1={21} x2={15} y2={15}></line>
                </svg>
              </div>

              <input
                ref={searchInput}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSearchSubmit() } }}
                value={search}
                type="text"
                placeholder={`${lang.search} Pembelian...`}
                autoComplete="off"
                className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              />
            </div>

            {/* <Filter
              // list_flow={list_flow}
              onSubmit={updateFilter}
              onRemove={removeFilter}
              data={Object.fromEntries(Object.entries({ status }).filter(([_, v]) => v != null))}
            /> */}

            <OutletFilterSelect
              value={outletSelectValue}
              onChange={(v) => patchSearchParams({ outlet_id: v })}
              disabled={isBranchLocked}
              options={outletOptions}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            />

            <select
              value={status || ""}
              onChange={(e) => patchSearchParams({ status: e.target.value })}
              className="hidden sm:block py-3 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            >
              <option value="">All Status</option>
              <option value="DRAFT">DRAFT</option>
              <option value="SUBMITTED">SUBMITTED</option>
              <option value="APPROVED">APPROVED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="RECEIVED">RECEIVED</option>
              <option value="PARTIAL_RECEIVED">PARTIAL_RECEIVED</option>
              <option value="CLOSED">CLOSED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>

            {/* 150726 (ATALLA-322): tombol tanda tanya penjelasan status PO. */}
            <StatusInfo
              className="hidden sm:inline-flex self-center"
              statuses={PO_STATUS_INFO}
            />

            {/* 230726 (permintaan user): filter tanggal dipindah ke popover — toolbar
                tinggal Search + Outlet + Status. Titik oranye = filter tanggal aktif. */}
            <div className="relative hidden sm:block">
              <button
                type="button"
                onClick={() => setDateFilterOpen((v) => !v)}
                className={`inline-flex items-center gap-2 py-3 px-4 text-xs transition border rounded-xl focus:outline-none ${
                  dateFrom || dateTo
                    ? "border-neutral-400 text-neutral-900 font-semibold"
                    : "border-neutral-200 text-neutral-700"
                }`}
              >
                <span>Tanggal</span>
                {(dateFrom || dateTo) && <span className="w-2 h-2 rounded-full bg-brand-500" />}
              </button>

              {dateFilterOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setDateFilterOpen(false)} />
                  <div className="absolute left-0 z-20 p-4 mt-2 space-y-3 bg-white border border-neutral-200 rounded-xl shadow-lg w-60">
                    <div>
                      <label className="block mb-1 text-xs text-neutral-500">Dari</label>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => patchSearchParams({ date_from: e.target.value })}
                        className="w-full py-2 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs text-neutral-500">Sampai</label>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => patchSearchParams({ date_to: e.target.value })}
                        className="w-full py-2 px-3 text-xs transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400"
                      />
                    </div>
                    {(dateFrom || dateTo) && (
                      <button
                        type="button"
                        onClick={() => patchSearchParams({ date_from: "", date_to: "" })}
                        className="w-full py-2 text-xs transition border border-neutral-200 rounded-xl hover:bg-neutral-50"
                      >
                        Reset Tanggal
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* 230726 (permintaan user): dua tombol import digabung ke satu dropdown.
                Panel disembunyikan via CSS `hidden` (BUKAN unmount) supaya Modal milik
                masing-masing komponen import tetap hidup saat panel ditutup
                (Modal headlessui = portal, aman dari display:none parent). */}
            {can("purchase-order.create") && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setImportMenuOpen((v) => !v)}
                  className="inline-flex items-center px-4 py-3 space-x-2 text-xs transition border border-neutral-200 bg-neutral-50 rounded-xl active:hover:scale-90"
                >
                  <span>Import</span>
                  <span className="text-neutral-500">▾</span>
                </button>

                {importMenuOpen && (
                  <div className="fixed inset-0 z-10" onClick={() => setImportMenuOpen(false)} />
                )}
                <div
                  className={`${importMenuOpen ? "" : "hidden"} absolute right-0 z-20 flex flex-col items-stretch gap-2 p-2 mt-2 bg-white border border-neutral-200 rounded-xl shadow-lg w-64`}
                  onClick={() => setImportMenuOpen(false)}
                >
                  <ImportFactorySuratJalan
                    currentBranch={currentBranch}
                    supplierSelection={supplierSelection}
                    userName={user?.name}
                    onSuccess={mutate}
                  />
                  <ImportLegacyDO
                    branchSelection={branchSelection}
                    currentBranch={currentBranch}
                    supplierSelection={supplierSelection}
                    userName={user?.name}
                    onSuccess={mutate}
                  />
                </div>
              </div>
            )}

            {can("purchase-order.create") && (
              <div>
                <Link
                  to="create"
                  onClick={() => setTransition({ type: "slide", reverse: true })}
                  state={{ back: title, from: location?.pathname, transition: { type: "slide" } }}
                  className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-brand-800 rounded-xl active:hover:scale-90"
                >
                  <Plus className="w-4 h-4" stroke={1.5} />
                  <span>{lang.create}</span>
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto border border-neutral-200 rounded-xl">
          <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
            <thead className="bg-neutral-50 rounded-t-3xl">
              <tr>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">PO No</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Outlet</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Type</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Supplier</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Date</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Status</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Items</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Qty</th>
                <th className="relative px-3 py-3"><span className="sr-only">{lang.action}</span></th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-neutral-200">
              {isLoading && (
                <tr className="text-center">
                  <td colSpan={9} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.loading_data}
                  </td>
                </tr>
              )}

              {!isLoading && list.length === 0 && !searchParam && (
                <tr className="text-center">
                  <td colSpan={9} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_data}
                  </td>
                </tr>
              )}

              {!isLoading && list.length === 0 && searchParam && (
                <tr className="text-center">
                  <td colSpan={9} className="px-3 py-6 text-xs text-neutral-500 whitespace-nowrap">
                    {lang.no_result}
                  </td>
                </tr>
              )}

              {!isLoading && list.map((po) => (
                <tr key={po.id} className="hover:bg-brand-50 transition-colors">
                  <td className="px-3 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                    
                    {po.faktur_no ? (
                      <div>Faktur: {po.faktur_no}</div>
                    ) : null}
                    {po.do_no ? (
                      <div className="text-[11px]">DO: {po.do_no}</div>
                    ) : null}
                    <div className="text-[11px] font-normal">{po.po_number ?? "-"}</div>
                    {/* 240726 (permintaan user): nomor DO & Faktur yang sudah ter-generate
                        tampil di bawah nomor PO (kosong bila belum pernah dicetak). */}
                    
                  </td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {branchNameById[String(po.outlet_id)] ?? po.outlet_id ?? "-"}
                  </td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {po.po_type ?? "-"}
                  </td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {po.supplier_name ?? po.supplier_name_snapshot ?? po.supplier_id ?? "-"}
                  </td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {formatDateTime(po.po_date) ?? "-"}
                  </td>
                  <td className="px-3 py-4 text-xs whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {statusBadge(po.status)}
                      {String(po.status || "").toUpperCase() === "REJECTED" && po.remarks ? (
                        <span
                          className="text-[11px] text-red-600 whitespace-normal max-w-[220px]"
                          title={po.remarks}
                        >
                          {po.remarks}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {po.items_count ?? "-"}
                  </td>
                  <td className="px-3 py-4 text-xs text-neutral-700 whitespace-nowrap">
                    {qtyFmt(po.status === "CLOSED" ? po.qty_received_total : po.qty_total)}
                  </td>

                  <td className="px-3 py-4 text-xs font-medium text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      {(can("purchase-order.update") || can("purchase-order.approve")) && (
                        <Link
                          to={`${po.id}/edit`}
                          state={{ back: title, from: location?.pathname, transition: "slide" }}
                          className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 active:hover:scale-90"
                        >
                          <Pencil className="w-6 h-6" />
                        </Link>
                      )}

                      {/* {can("purchase-order.approve") && canApprove(po) && (
                        <button
                          type="button"
                          disabled={actingId === po.id}
                          onClick={() => onApprove(po)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-500 disabled:opacity-60"
                        >
                          {actingId === po.id ? "..." : "Approve"}
                        </button>
                      )} */}

                      {canReceiveRemainder(po) && (
                        <button
                          type="button"
                          disabled={actingId === po.id}
                          onClick={() => onReceiveRemainder(po)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-500 disabled:opacity-60"
                          title="Buat GR draft berisi sisa item yang belum diterima"
                        >
                          {actingId === po.id ? "..." : "Terima Sisa"}
                        </button>
                      )}

                      {can("purchase-order.create") && (
                        <button
                          type="button"
                          disabled={actingId === po.id}
                          onClick={() => onCopyPo(po)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 disabled:opacity-60"
                          title="Buat PO draft baru berisi produk yang sama"
                        >
                          {actingId === po.id ? "..." : "Copy"}
                        </button>
                      )}

                      {can("purchase-order.close") && canClose(po) && (
                        <button
                          type="button"
                          disabled={actingId === po.id}
                          onClick={() => onClose(po)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-brand-600 rounded-lg hover:bg-neutral-600 disabled:opacity-60"
                        >
                          {actingId === po.id ? "..." : "Close"}
                        </button>
                      )}

                      {canPrint(po) && (
                        <PurchaseExportActions poId={po.id} poStatus={po.status} doPrinted={!!po.do_no} compact />
                      )}


                      {/* {can("purchase-order.cancel") && canCancel(po) && (
                        <button
                          type="button"
                          disabled={actingId === po.id}
                          onClick={() => onCancel(po)}
                          className="px-3 py-2 text-[11px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-500 disabled:opacity-60"
                        >
                          {actingId === po.id ? "..." : "Cancel"}
                        </button>
                      )} */}

                      {/* {can("purchase-order.delete") && (
                        <Delete data={po} onSuccess={mutate} />
                      )} */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          params={generatePaginationParam()}
          links={paginationLinks}
          from={from}
          to={to}
          total={total}
        />

        {/* 220726: modal Copy PO — pilih Supplier & Outlet tujuan sebelum draft dibuat. */}
        <Modal
          isOpen={!!copyPo}
          onClose={closeCopyModal}
          title={`Copy PO ${copyPo?.po_number || copyPo?.id || ""}`}
          content={(
            <form onSubmit={submitCopyPo} className="mt-6 space-y-5 text-xs">
              <p className="text-neutral-500">
                PO baru berstatus DRAFT akan dibuat berisi produk yang sama. Pilih supplier dan
                outlet tujuan untuk PO baru (default mengikuti PO sumber).
              </p>
              {copyExisting.length > 0 && (
                <div className="p-3 border border-amber-200 bg-amber-50 rounded-xl text-amber-800 space-y-1">
                  <p className="font-semibold">
                    Perhatian: PO ini sudah pernah dicopy ({copyExisting.length} copy aktif).
                  </p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {copyExisting.map((c) => (
                      <li key={c.id}>
                        {c.po_number || c.id} — {String(c.status || "").toUpperCase()}
                      </li>
                    ))}
                  </ul>
                  <p>Pastikan tidak dobel order sebelum melanjutkan.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="block font-medium text-neutral-700">Supplier</label>
                  <select
                    value={copySupplierId}
                    onChange={(event) => setCopySupplierId(event.target.value)}
                    className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                  >
                    <option value="">Pilih Supplier</option>
                    {(supplierSelection || []).filter((s) => s?.id).map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="block font-medium text-neutral-700">Outlet Tujuan</label>
                  <select
                    value={copyOutletId}
                    onChange={(event) => setCopyOutletId(event.target.value)}
                    className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                  >
                    <option value="">Pilih Outlet</option>
                    {(allOutlets || []).map((branch) => (
                      <option key={branch.id} value={branch.id}>{branch.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={copySubmitting}
                  className="px-5 py-3 text-xs font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-500 disabled:opacity-60"
                >
                  {copySubmitting ? "Memproses..." : "Copy PO"}
                </button>
                <button
                  type="button"
                  disabled={copySubmitting}
                  onClick={closeCopyModal}
                  className="px-5 py-3 text-xs font-semibold border border-neutral-200 rounded-xl text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
                >
                  Batal
                </button>
              </div>
            </form>
          )}
        />
      </div>
    </Transition>
  )
}
